<script setup lang="ts">
import usePoolStore from '~/store/pool'
import { RealtimeState } from '~/interfaces/poolDetail'
import type { PoolStationEvse } from '~/interfaces/station'
import type { PropType } from 'vue'
import type { PoolStationConnector } from '#cmui/types'

const props = defineProps({
    connector: {
        type: Object as PropType<PoolStationConnector>,
        required: true
    },
    stationId: {
        type: Number,
        required: true
    },
    evse: {
        type: Object as PropType<PoolStationEvse>,
        required: true
    }
})

const poolStore = usePoolStore()

const isOwnerOrOperatorNameFilled = computed(
    () => !!(poolStore.getOwnerName || props.connector?.operatorName)
)

const getOperatorName = computed(() => props.connector?.operatorName || '')

const getEvse = computed<string>(
    () => props.evse.defaultEvseForCharging?.toString() || ''
)

const onConnectorChange = (event: Event) => {
    poolStore.updateConnectorState(
        Number((event.target as HTMLInputElement)?.value) || 0,
        props.stationId,
        props.connector.id
    )
}

const isConnectorAvailable = (connector: any): number =>
    +(connector.realtimeStatus === RealtimeState.AVAILABLE)
</script>

<template>
    <div
        class="w-full bg-yellow-l-warning gap-y-1 rounded-lg py-2 px-4 text-xs min-h-50 flex items-center font-medium flex-wrap"
    >
        <div v-if="isOwnerOrOperatorNameFilled">
            <span class="text-orange-l-secondary">
                {{ $t('word.operator') }} :&nbsp;
            </span>
            <span v-if="getOperatorName" class="text-green-l-secondary">
                {{ getOperatorName }}
            </span>
            <span v-else class="text-green-l-secondary">
                {{ poolStore.getOwnerName }}
            </span>
            <span class="text-orange-l-secondary">&nbsp;/&nbsp;</span>
        </div>
        <div v-if="getEvse">
            <span class="text-orange-l-secondary">EVSE :&nbsp;</span>
            <span
                v-if="evse.defaultEvseForCharging"
                class="text-brown-l-primary"
            >
                {{ evse.defaultEvseForCharging }}
            </span>
            <span class="text-orange-l-secondary">&nbsp;-&nbsp;</span>
        </div>
        <div>
            <span class="text-orange-l-secondary">
                {{ $t('word.connector') }} :&nbsp;
            </span>
            <span class="text-brown-l-primary">{{ connector.id }}</span>
            <span class="text-orange-l-secondary">&nbsp;/&nbsp;</span>
        </div>
        <div>
            <span class="text-brown-l-primary">
                {{ $t('word.state') }}&nbsp;
            </span>
            <select
                class="ml-1 rounded-lg p-1 border-1 border-gray-l-300"
                :value="isConnectorAvailable(connector)"
                @change="onConnectorChange"
            >
                <option value="0">{{ $t('plug.state.out_of_service') }}</option>
                <option value="1">{{ $t('plug.state.functional') }}</option>
            </select>
        </div>
    </div>
</template>
